// viewers/PdfViewer.tsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
import PdfToImageOCRWrapper from "@/utils/PdfToImageOCRWrapper";
import type { PreviewPdfDocument } from "../actions/pdfPageDownload";



/** escape regex metacharacters */
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Turn a keyword/phrase into a regex that tolerates weird spacing */
const phraseToPattern = (s: string) => {
  // split on any whitespace and rejoin with a flexible whitespace class
  const parts = s.trim().split(/\s+/).map(esc).filter(Boolean);
  return parts.length ? parts.join("[\\s\\u00A0]+") : "";
};

const makeMatcher = (words: string[], flags = "giu") => {
  const alts = words.map(phraseToPattern).filter(Boolean);
  return alts.length ? new RegExp(`(?<!\\w)(?:${alts.join("|")})(?!\\w)`, flags) : null;
};



type Props = {
  url: string;
  keywords?: string[];
  allowOCRFallback?: boolean; // default true
  onPdfReady?: (pdf: PreviewPdfDocument | null) => void;
};

export default function PdfViewer({
  url,
  keywords = [],
  allowOCRFallback = true,
  onPdfReady,
}: Props) {
  const [numPages, setNumPages] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState(800);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [hasHighlights, setHasHighlights] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  // Build the matcher once per keywords change
  const rx = useMemo(() => makeMatcher(keywords), [keywords]);

  // For quick boolean check vs full text (no 'g' so .test() is stable)
  const rxTest = useMemo(() => makeMatcher(keywords, "iu"), [keywords]);

  // For inline highlighting (note: can only match within a single text item)
  const rxReplace = useMemo(() => makeMatcher(keywords, "giu"), [keywords]);

  const onResize = () => setContainerWidth(ref.current?.clientWidth || 800);

  // Keyword changes should not clear the PDF text; only loading a new URL does.
  useEffect(() => {
    setHasHighlights(false);
    const timer = setTimeout(() => {
      onResize();
    }, 500);
    return () => clearTimeout(timer);
  }, [url, keywords]);

  // Responsive width
  useEffect(() => {
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Extract all page text (to decide between OCR vs text layer)
  useEffect(() => {
    let aborted = false;
    const loadingTask = pdfjs.getDocument(url);
    onPdfReady?.(null);
    (async () => {
      try {
        setExtractedText(null);
        const pdf = await loadingTask.promise;
        if (aborted) return;
        onPdfReady?.({ url, numPages: pdf.numPages, getData: () => pdf.getData() });

        const pages = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
        const chunks: string[] = [];
        for (const n of pages) {
          if (aborted) return;
          const page = await pdf.getPage(n);
          const content = await page.getTextContent();
          chunks.push((content.items as any[]).map(it => (it as any).str).join(" "));
          page.cleanup();
        }
        if (!aborted) setExtractedText(chunks.join("\n").trim());
      } catch {
        if (!aborted) setExtractedText(""); // force OCR fallback route
      }
    })();
    return () => {
      aborted = true;
      onPdfReady?.(null);
      void loadingTask.destroy().catch(() => {});
    };
  }, [url, onPdfReady]);

// Normalize extracted text for matching
const normalizedText = useMemo(() => {
  if (extractedText == null) return null;
  // convert NBSP to space, collapse whitespace runs
  return extractedText.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();
}, [extractedText]);

// Decide if there are any matches in the extracted text
useEffect(() => {
  if (normalizedText == null) return;
  if (!rxTest) { setHasHighlights(false); return; }
  setHasHighlights(rxTest.test(normalizedText));
}, [normalizedText, rxTest]);

// Debug log of unique matches (on normalized text)
useEffect(() => {
  if (normalizedText && rx) {
    const m = normalizedText.match(rx);
    console.log("[PdfViewer] matched keywords:", Array.from(new Set(m || [])));
  }
}, [normalizedText, rx]);


// Show a spinner while parsing
if (extractedText === null) {
  return <div className="p-4">🔄 Parsing PDF text…</div>;
}

// Check for matches in the extracted, selectable text
const hasAnyText = !!(normalizedText && /\S/.test(normalizedText));
const hasAnyMatch = !!(hasAnyText && rxTest?.test(normalizedText));

const hasTextMatches = !!(rxTest && extractedText && rxTest.test(extractedText));

if (keywords.length !== 0 || hasTextMatches) {
// if (allowOCRFallback && keywords.length > 0 && hasTextMatches) {
  return (
    <div ref={ref} className="overflow-y-auto overflow-x-hidden h-[79vh]">
      <PdfToImageOCRWrapper pdfUrl={url} searchKeywords={keywords} showPageNumbers />
    </div>
  );
}

// Otherwise, render the normal selectable text layer with (optional) highlights
const customTextRenderer = ({ str }: { str: string }) =>
  rxReplace ? str.replace(rxReplace, (m) => `<span class="pdf-highlight">${m}</span>`) : str;

return (
  <div ref={ref} className="overflow-y-auto overflow-x-hidden h-[79vh]">
    <Document file={url} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
      <div className="space-y-3 py-0">
        {Array.from({ length: numPages }, (_, i) => {
          const pageNumber = i + 1;
          return (
            <div
              key={`page_wrap_${pageNumber}`}
              className="relative mx-auto max-w/full bg-white shadow border border-gray-300 p-0 box-border"
              style={{ width: Math.floor(containerWidth) }}
            >
              <div className="px-3 py-1 text-xs text-gray-600 bg-gray-100">
                Page {pageNumber} of {numPages}
              </div>
              <Page
                key={`page_${pageNumber}`}
                pageNumber={pageNumber}
                width={Math.floor(containerWidth)}
                renderTextLayer
                customTextRenderer={customTextRenderer}
              />
            </div>
          );
        })}
      </div>
    </Document>
  </div>
);
}
